<template>
  <v-dialog
    v-model="dialog"
    :max-width="maxWidth"
  >
    <template #activator="{ on: dialogOn, attrs: dialogAttrs }">
      <ActivatorButton
        v-if="buttonTitle"
        :parent-attrs="dialogAttrs"
        :parent-on="dialogOn"
        :icon-or-title="buttonTitle"
        :tooltip-text="title"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        @activate="onButtonActivate"
        :v-btn-props="{ class: 'flat-button border' }"
      />
    </template>

    <v-form ref="form">
      <v-card>
        <v-card-title>{{ title }}</v-card-title>
        <v-card-subtitle
          v-if="true ?? warning"
          class="text-danger font-weight-bold"
        >
          {{ warning }}
        </v-card-subtitle>

        <v-card-text>
          <v-container>
            <v-row v-if="getCheckedStructures.length === 0">
              <v-col cols="12">
                <v-file-input
                  v-model="files"
                  :label="label"
                  show-size
                  :multiple="multiple"
                  :rules="[(v) => !!v.length || 'Required']"
                />
              </v-col>
            </v-row>

            <v-row
              v-for="fileHeaders in filesHeaders"
              :key="fileHeaders.fileName"
            >
              <v-col>
                {{ fileHeaders.fileName }}
                <v-col
                  v-b-tooltip
                  title="Column with values, that have to be used as key for search"
                  :disabled="getCheckedStructures.length != 0"
                >
                  <v-select
                    v-model="keyName"
                    :items="[...fileHeaders.headers]"
                    label="Keys Column"
                  />
                </v-col>
                <v-col
                  v-b-tooltip
                  title="Type of values, stored in 'Keys column'. 'InChIKey1' works same as 'InChIKey' for Commonchemistry"
                >
                  <v-select
                    v-model="keyType"
                    :items="['InChIKey', 'InChIKey1', 'CASRN', 'SMILES', 'Name', 'Pubchem_CID']"
                    label="Key Type"
                  />
                </v-col>
              </v-col>
            </v-row>

            <v-row v-if="getCheckedStructures.length != 0">
              <v-col>
                Harvest data from ChemCart
                <v-col
                  v-b-tooltip
                  title="Type of key for harvesting data from ChemCart"
                >
                  <v-select
                    v-model="keyType"
                    :items="['InChIKey', 'InChIKey1', 'CASRN', 'SMILES', 'Name']"
                    label="Key Type"
                  />
                </v-col>
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col cols="4">
                <v-checkbox
                  id="use-pubchem"
                  v-model="usePubchem"
                  label="Use Pubchem"
                  hide-details
                />
              </v-col>
              <v-col cols="8">
                <v-checkbox
                  id="load-names"
                  v-model="loadNames"
                  label="Load Names"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col cols="4">
                <v-checkbox
                  id="use-commonchemistry"
                  v-model="useCommonchemistry"
                  label="Use Commonchemistry"
                  hide-details
                />
              </v-col>
              <v-col cols="8">
                <v-checkbox
                  id="load-casrns"
                  v-model="loadCASRNs"
                  label="Load CASRNs"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col cols="4">
                <v-checkbox
                  id="use-resolver"
                  v-model="useResolver"
                  label="Use Resolver"
                  hide-details
                />
              </v-col>
              <v-col
                cols="8"
                v-b-tooltip
                title="no DeletedCASRNs for Resolver"
              >
                <v-checkbox
                  id="load-deleted-casrns"
                  v-model="loadDeletedCASRNs"
                  label="Load DeletedCASRNs"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col
                cols="4"
                v-b-tooltip
                title="If 'Deep search' selected - search will be resolved directly in Pubchem/Commonchemistry databases, if not - only in our repository only"
              >
                <v-checkbox
                  id="deep-search"
                  v-model="deepSearch"
                  label="Deep Search"
                  hide-details
                />
              </v-col>

              <v-col cols="8">
                <v-checkbox
                  id="load-inchikeys"
                  v-model="loadInChIKeys"
                  label="Load InChIKeys"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col cols="4" />

              <v-col
                cols="8"
                v-b-tooltip
                title="no DTXSIDs for Commonchemistry"
              >
                <v-checkbox
                  id="load-dtxsids"
                  v-model="loadDTXSIDs"
                  label="Load DTXSIDs"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="filesHeaders[0] || getCheckedStructures.length != 0">
              <v-col cols="4" />

              <v-col cols="8">
                <v-checkbox
                  id="load-smiless"
                  v-model="loadSMILESs"
                  label="Load SMILESs"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="usePubchem && (filesHeaders[0] || getCheckedStructures.length != 0)">
              <v-col cols="4" />

              <v-col
                cols="8"
                v-b-tooltip
                title="count of patents, literature and references from Pubchem"
              >
                <v-checkbox
                  id="load-totals"
                  v-model="loadTotals"
                  label="Load Total Counts from Pubchem"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-if="usePubchem && (filesHeaders[0] || getCheckedStructures.length != 0)">
              <v-col cols="4" />

              <v-col
                cols="8"
                v-b-tooltip
                title="Chemical Vendors assigned in Pubchem"
              >
                <v-checkbox
                  id="load-vendors"
                  v-model="loadVendors"
                  label="Load Chemical Vendors from Pubchem"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row
              v-if="message"
              style="color: red; font-size: larger"
            >
              {{ message }}
            </v-row>
          </v-container>
          <v-progress-linear
            v-if="uploading"
            indeterminate
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="uploading"
            @click="dialog = false"
            aria-label="Cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="
              uploading ||
              (this.getCheckedStructures && keyType == null) ||
              (files.length && (keyName == null || keyType == null)) ||
              (!files.length && !this.getCheckedStructures)
            "
            @click="onUpload"
            aria-label="Upload"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import DialogBase from '@/components/dialogs/DialogBase.vue'
import { getErrorMessage, download } from '@/main'
import { mapGetters } from 'vuex'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import ActivatorButton from '@/components/ActivatorButton.vue'

export default {
  components: { ActivatorButton },
  mixins: [DialogBase],

  props: {
    preflightUrl: {
      type: String,
      default: null,
    },
    uploadUrl: {
      type: String,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'TXT, CSV or XLSX file',
    },
    warning: {
      type: String,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    comment: null,
    uploading: false,
    filesHeaders: [],
    localFiles: [],
    keyName: null,
    keyType: null,
    loadNames: false,
    loadDeletedCASRNs: false,
    loadInChIKeys: true,
    loadCASRNs: true,
    loadDTXSIDs: false,
    loadSMILESs: false,
    loadTotals: false,
    loadVendors: false,
    usePubchem: true,
    useCommonchemistry: false,
    useResolver: false,
    deepSearch: false,
    defaultVTooltipProps,
  }),

  computed: {
    ...mapGetters(['getCheckedStructures']),

    files: {
      get() {
        return Array.isArray(this.localFiles) ? this.localFiles : [this.localFiles]
      },
      set(val) {
        Array.isArray(val) ? (this.localFiles = val) : (this.localFiles = [val])
      },
    },
    hasFileWithFields() {
      return this.files.find(
        (f) =>
          f.name.toLowerCase().endsWith('.txt') ||
          f.name.toLowerCase().endsWith('.csv') ||
          f.name.toLowerCase().endsWith('.xlsx'),
      )
    },
  },

  watch: {
    files() {
      this.filesHeaders = []
      if (this.preflightUrl && this.hasFileWithFields) {
        let formData = new FormData()

        this.files.forEach((f) => {
          if (
            f.name.toLowerCase().endsWith('.txt') ||
            f.name.toLowerCase().endsWith('.csv') ||
            f.name.toLowerCase().endsWith('.xlsx')
          ) {
            formData.append('files[]', f, f.name)
          }
        })

        this.message = null
        this.uploading = true

        this.$axios
          .post(this.preflightUrl, formData)
          .then((response) => {
            this.uploading = false
            if (Array.isArray(response.data) && response.data.length) {
              this.filesHeaders = response.data
            } else {
              this.message = 'Cannot read headers'
            }
          })
          .catch((error) => {
            this.uploading = false
            this.message = getErrorMessage(error)
          })
      }
    },
  },

  methods: {
    onUpload() {
      if (((this.files && this.files.length) || this.getCheckedStructures.length) && this.$refs.form.validate()) {
        this.uploading = true

        let formData = new FormData()
        if (this.files && this.files.length && !this.getCheckedStructures.length)
          for (let file of this.files) formData.append('files[]', file, file.name)

        let infoO = {
          keyName: this.keyName,
          keyType: this.keyType,
          loadNames: this.loadNames,
          loadDeletedCASRNs: this.loadDeletedCASRNs,
          loadInChIKeys: this.loadInChIKeys,
          loadCASRNs: this.loadCASRNs,
          loadDTXSIDs: this.loadDTXSIDs,
          loadSMILESs: this.loadSMILESs,
          loadTotals: this.loadTotals,
          loadVendors: this.loadVendors,
          usePubchem: this.usePubchem,
          useResolver: this.useResolver,
          useCommonchemistry: this.useCommonchemistry,
          deepSearch: this.deepSearch,
        }

        formData.append('request', JSON.stringify({ info: infoO }))

        this.$axios
          .post(
            this.getCheckedStructures.length ? this.uploadUrl + '_cart' : this.uploadUrl,
            this.getCheckedStructures.length ? { info: infoO, chemicals: this.getCheckedStructures } : formData,
            { responseType: 'blob' },
          )
          .then((response) => {
            this.uploading = false
            this.dialog = false
            let ext = 'xlsx'
            let file_name = 'chemcart_universalharvest.' + ext

            if (!this.getCheckedStructures.length) {
              console.log(this.filesHeaders[0])
              let i = this.filesHeaders[0].fileName.lastIndexOf('.')
              ext = this.filesHeaders[0].fileName.substring(i + 1)
              if (ext === 'txt' || ext === 'csv') ext = 'xlsx'
              file_name = this.filesHeaders[0].fileName.substring(0, i) + '_universalharvest.' + ext
              console.log(file_name)
            }

            download(file_name, response.data, ext)
          })
          .catch((error) => {
            this.uploading = false
            this.message = getErrorMessage(error)
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-input--selection-controls {
  margin-top: 0;
}

.row + .row {
  margin-top: 0;
}
</style>
